import type { Query } from "@tanstack/react-query";

import {
  type MultiversionTestRunResults,
  type MultiversionTestRunVersionResults,
  RouterMultiversionTestRun,
} from "src/api/endpoints";
import { GraphRunResultType } from "src/api/types";

const POLL_INTERVAL_MS = 1000;

export const multiversionTestRunKeys = {
  detail: (baseUrl: string, testRunId: string) =>
    ["multiversionTestRun", baseUrl, testRunId] as const,
};

/**
 * Every version has reported results (a success or a graph error); the run
 * API stops changing the record from here on.
 */
export const isMultiversionTestRunFinished = (
  run: MultiversionTestRunResults,
): boolean =>
  run.flow_versions.every((version) => version.execution_results !== null);

/**
 * The version whose results a link should land on: the preferred one when it
 * took part in the run, else the first one that ran successfully, else the
 * first one at all.
 */
export const pickTestRunVersion = (
  run: MultiversionTestRunResults,
  preferredVersionId?: string,
): MultiversionTestRunVersionResults | undefined =>
  run.flow_versions.find((version) => version.id === preferredVersionId) ??
  run.flow_versions.find(
    (version) => version.execution_results?.type === GraphRunResultType.SUCCESS,
  ) ??
  run.flow_versions[0];

export const fetchMultiversionTestRun = async (
  baseUrl: string,
  testRunId: string,
): Promise<MultiversionTestRunResults> => {
  const response = await RouterMultiversionTestRun.get(baseUrl, testRunId);
  if (response.status !== 200) {
    throw new Error(
      `Got an error ${response.status} while fetching test run ${testRunId}`,
    );
  }
  return response.data;
};

const isSettled = (query: Query<MultiversionTestRunResults, Error>): boolean =>
  query.state.status === "error" ||
  (query.state.data !== undefined &&
    isMultiversionTestRunFinished(query.state.data));

/**
 * One router test run (`GET /run/api/v1/test-runs/{id}`), shared by the
 * analytics pane and the copilot's test-run deeplink. An observer polls until
 * the run settles; `ensureQueryData` installs no observer, so a warm is a
 * single fetch.
 */
export const multiversionTestRunQueryOptions = (
  baseUrl: string,
  testRunId: string,
) => ({
  queryKey: multiversionTestRunKeys.detail(baseUrl, testRunId),
  queryFn: () => fetchMultiversionTestRun(baseUrl, testRunId),
  refetchInterval: (query: Query<MultiversionTestRunResults, Error>) =>
    isSettled(query) ? false : POLL_INTERVAL_MS,
  // A run in flight advances whether or not the tab is watched, and its
  // results are wanted on return rather than on refocus.
  refetchIntervalInBackground: true,
  // A finished run never changes again, and re-reading one past the
  // workspace's retention cap answers 408 — which would put this shared entry
  // into error state under everyone still showing the results.
  refetchOnMount: (query: Query<MultiversionTestRunResults, Error>) =>
    query.state.data === undefined ||
    !isMultiversionTestRunFinished(query.state.data),
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
});
